import React, { useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import type { Feature } from 'geojson';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import Button from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import { CustomAlert } from 'components/AlertsCollection';
import { PolygonLayerFeatureProperties } from 'components/InteractiveMap/PolygonLayer';
import MapSpinnerControl from 'components/Meteorology/Fields/ModelPerformance/Overview/MapDisplay/MapSpinnerControl';
import { ModelPerformanceMapData } from 'types/Meteorology/Fields/ModelPerformance/mapData';
import { ModelPerformanceMapError } from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import useMapDownload from 'hooks/Meteorology/Fields/ModelPerformance/useMapDownload';
import useMapTitle from 'hooks/Meteorology/Fields/ModelPerformance/useMapTitle';
import { cssVar } from 'constants/theme';

import './index.scss';

export interface MapDisplayProps {
  mapData: ModelPerformanceMapData | null;
  mapLoading: boolean;
  mapError: ModelPerformanceMapError | undefined;
  selectedFeatureName: string | null;
  onPolygonClick: (feature: Feature, properties: PolygonLayerFeatureProperties) => void;
  onClearSelection: () => void;
  onOpenLegend: () => void;
}

export default function MapDisplay({
  mapData,
  mapLoading,
  mapError,
  selectedFeatureName,
  onPolygonClick,
  onClearSelection,
  onOpenLegend,
}: MapDisplayProps) {
  const {
    onDownloadClick, setOnDownloadClick, filename, isDownloadDisabled,
  } = useMapDownload();
  const { title, subtitle, placeholder } = useMapTitle();
  const [fullscreen, setFullscreen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const hasValidData = mapData != null && mapData.geoJson?.features?.length > 0;

  const alert: CustomAlert | undefined = mapError ? {
    title: mapError.title,
    message: mapError.message,
    variant: 'danger',
  } : undefined;

  return (
    <Card
      ref={cardRef}
      className={`mp-mobile-map-display shadow-sm overflow-hidden${fullscreen ? ' mp-mobile-map-display-fullscreen' : ''}`}
    >
      {hasValidData && (title || subtitle) && (
        <div className="mp-mobile-map-display-title">
          {title && <span className="mp-mobile-map-display-title-text">{title}</span>}
          {subtitle && <span className="mp-mobile-map-display-subtitle-text">{subtitle}</span>}
        </div>
      )}
      <div className="mp-mobile-map-display-header">
        <button
          type="button"
          className="mp-mobile-map-display-legend"
          onClick={onOpenLegend}
          disabled={!hasValidData}
        >
          Legenda
        </button>
        <div className="mp-mobile-map-display-header-actions">
          <Button
            icon="download"
            type="tertiary"
            small
            title="Baixar mapa"
            onClick={onDownloadClick}
            disabled={isDownloadDisabled()}
            className="mp-mobile-map-display-download"
          />
        </div>
      </div>
      {fullscreen && (
        <button
          type="button"
          aria-label="Sair da tela cheia"
          className="mp-mobile-map-display-fullscreen-close"
          onClick={() => setFullscreen(false)}
        >
          <Icon iconType="close" color={cssVar('text-strong')} />
        </button>
      )}
      <div className="mp-mobile-map-display-map-wrapper">
        {!fullscreen && (
          <button
            type="button"
            aria-label="Tela cheia"
            className="mp-mobile-map-display-fullscreen-float"
            onClick={() => setFullscreen(true)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke={cssVar('text-strong')}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
          </button>
        )}
        <InteractiveMap
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        >
          <InteractiveMap.MapClickHandler onClick={onClearSelection} />
          <InteractiveMap.MapUpdater observedDivRef={cardRef} />
          <MapSpinnerControl isLoading={mapLoading} />
          <InteractiveMap.Alert alert={alert} />
          {hasValidData && mapData && (
            <>
              <InteractiveMap.PolygonLayer
                data={mapData.geoJson}
                onPolygonClick={onPolygonClick}
                selectedFeatureName={selectedFeatureName}
              />
              <InteractiveMap.ImageDownloader
                setOnDownload={setOnDownloadClick}
                filename={filename}
                caption={placeholder}
              />
            </>
          )}
          <BrazilStatesLayer />
        </InteractiveMap>
      </div>
    </Card>
  );
}
